// „Настани" на резервация с няколко маси: една сметка или отделна за всяка
// маса — чиста логика без React (тестове: seatBills.test.ts). Огледало на BE
// `SeatingService.seat` с `separateBills` (app/features/reservations/
// service_seating.py `_separate_bills_plan`): главната сметка е избраната
// (`billId`) или на първата маса, а сметките на другите маси са също на
// резервацията (`billIds`).

import type { OpenTable, Reservation } from "../../../api/reservations";
import { seatPlanOf, type SeatPlan } from "./reservationActions";
import { tableNameKey } from "./tableStatus";

/** Как да се отворят сметките: една (по подразбиране) или по една на маса. */
export type SeatBillsMode = "single" | "separate";

/** Сметката на една маса при „Отделна сметка за всяка маса". */
export interface TableSeatBill {
  tableName: string;
  // Отворената сметка на масата (закачане); null — ще се отвори нова.
  bill: OpenTable | null;
}

/** Защо „Отделна сметка за всяка маса" не може изобщо (null — може). */
// Локален режим срещу касата: там „Настани" отваря само една сметка.
export type SeparateBillsBlock = "localMode";

/** Масата и отворените сметки по нея (без тези на други резервации). */
export interface TableBills {
  tableName: string;
  bills: OpenTable[];
}

export interface SeatBillsChoice {
  // „Една сметка" — каквото „Настани" прави и днес: нова на първата маса,
  // закачане към единствената отворена или избор при кой сервитьор.
  single: SeatPlan;
  // Масата на сметката при „Една сметка" (null — изборът е между няколко).
  singleTable: string | null;
  // Масите по реда им и сметките по всяка — за „Отделна…" (`separatePlanOf`).
  tables: TableBills[];
  separateBlock: SeparateBillsBlock | null;
}

/**
 * „Отделна сметка за всяка маса" с избраната сметка — огледало на BE
 * `_separate_bills_plan`:
 * - `ready` — по маса: избраната (на нейната маса), единствената отворена,
 *   от няколко — тази на сервитьора на избраната, иначе нова; `owner` —
 *   сервитьорът на новите сметки (избраната; null — натисналия, „ничия" до
 *   първата поръчка);
 * - `pick` — на маса има няколко отворени сметки, а сметка не е избрана;
 * - `noBillOfWaiter` — избраният сервитьор няма сметка на такава маса.
 */
export type SeparatePlan =
  | { kind: "ready"; bills: TableSeatBill[]; owner: OpenTable | null }
  | { kind: "pick"; table: string }
  | { kind: "noBillOfWaiter"; table: string; waiter: string | null };

/**
 * Различните маси на резервацията, по реда ѝ: маси с един и същ ключ на
 * името („2" и „2*") са една маса — огледало на `table_occupancy_key` в BE.
 * Общо с „Настани" от Маркиране (`ordering/components/tables/seat/orderingSeatBills.ts`).
 */
export const distinctTablesOf = (tableNames: string[]): string[] => {
  const seenKeys = new Set<string>();
  return tableNames.filter((name) => {
    const key = tableNameKey(name);
    if (!key || seenKeys.has(key)) return false;
    seenKeys.add(key);
    return true;
  });
};

/** Отворените сметки на една маса (по ключа на името). */
export const billsOnTableOf = (tableName: string, bills: OpenTable[]): OpenTable[] =>
  bills.filter((bill) => tableNameKey(bill.tableName) === tableNameKey(tableName));

/**
 * Изборът „Една сметка" / „Отделна сметка за всяка маса" — само за
 * резервация с поне две (различни) маси; иначе null и „Настани" е както
 * досега. `seatBills` — отворените сметки по масите, които не са на друга
 * резервация (`UpcomingActions.seatBills`).
 */
export const seatBillsChoiceOf = (
  reservation: Reservation,
  seatBills: OpenTable[],
  isLocalMode: boolean,
): SeatBillsChoice | null => {
  const tables = distinctTablesOf(reservation.tableNames);
  if (tables.length < 2) return null;

  const single = seatPlanOf(seatBills);
  const singleTable =
    single.kind === "newBill" ? tables[0] : single.kind === "attach" ? single.bill.tableName : null;
  return {
    single,
    singleTable,
    tables: tables.map((tableName) => ({ tableName, bills: billsOnTableOf(tableName, seatBills) })),
    separateBlock: isLocalMode ? "localMode" : null,
  };
};

/**
 * Сметките на масите с няколко отворени (напр. двама сервитьори на маса 25)
 * — само между тях „Отделна…" пита към коя; [] — няма какво да се избира.
 */
export const crowdedBillsOf = (choice: SeatBillsChoice): OpenTable[] =>
  choice.tables.filter((entry) => entry.bills.length > 1).flatMap((entry) => entry.bills);

/**
 * Планът на „Отделна…" с избраната сметка (`pickedBill`; null — без избор).
 * Изборът важи само ако е сметка от маса с няколко (`crowdedBillsOf`) —
 * иначе всяка маса си взима своята и нищо не се праща (`owner` е null).
 */
export const separatePlanOf = (choice: SeatBillsChoice, pickedBill: OpenTable | null): SeparatePlan => {
  const picked =
    pickedBill && crowdedBillsOf(choice).some((bill) => bill.billId === pickedBill.billId) ? pickedBill : null;
  const pickedKey = picked ? tableNameKey(picked.tableName) : null;
  const bills: TableSeatBill[] = [];
  for (const { tableName, bills: onTable } of choice.tables) {
    if (picked && tableNameKey(tableName) === pickedKey) {
      bills.push({ tableName, bill: picked });
      continue;
    }
    if (onTable.length <= 1) {
      bills.push({ tableName, bill: onTable[0] ?? null });
      continue;
    }
    if (!picked) return { kind: "pick", table: tableName };
    const own = onTable.filter((bill) => bill.waiterId === picked.waiterId);
    if (own.length !== 1) return { kind: "noBillOfWaiter", table: tableName, waiter: picked.waiterName };
    bills.push({ tableName, bill: own[0] });
  }
  return { kind: "ready", bills, owner: picked };
};

/**
 * Може ли „Настани" да тръгне с този избор: „Отделна…" — ако не е спряна и
 * планът ѝ е готов; „Една сметка" при няколко отворени сметки — едва след
 * избран сервитьор.
 */
export const canConfirmSeatBills = (
  choice: SeatBillsChoice,
  mode: SeatBillsMode,
  pickedBill: OpenTable | null,
): boolean => {
  const picked =
    choice.single.kind === "pick" && pickedBill !== null
      ? choice.single.bills.find((bill) => bill.billId === pickedBill.billId) ?? null
      : null;
  if (mode === "separate") return choice.separateBlock === null && separatePlanOf(choice, picked).kind === "ready";
  if (choice.single.kind !== "pick") return true;
  return picked !== null;
};
